import { useAuth } from './useAuth';

export const useGoogleStorageImage = () => {
  const bucket = process.env.REACT_APP_GOOGLE_CLOUD_STORAGE_BUCKET;
  const environment = process.env.REACT_APP_ENV;
  const { currentUser } = useAuth();
  const accessToken = currentUser?.credentials?.access_token;

  console.log('process.env', process.env);

  const gcloudUploadImage = async (file, includeBlob = true) => {
    if (!accessToken) {
      throw new Error('Authentication credentials not available');
    }

    // Use multipart upload to include metadata
    const metadata = {
      name: file.name,
      contentType: file.type || "application/octet-stream",
      metadata: {
        environment: environment,
        uploadedBy: currentUser?.userinfo?.email || 'unknown',
        uploadedByName: currentUser?.userinfo?.name || 'Unknown User',
        uploadedAt: new Date().toISOString(),
      }
    };

    const boundary = '-------314159265358979323846';
    const delimiter = "\r\n--" + boundary + "\r\n";
    const closeDelimiter = "\r\n--" + boundary + "--";

    // Read file as array buffer
    const fileContent = await file.arrayBuffer();
    
    // Build multipart request body
    const metadataPart = delimiter +
      'Content-Type: application/json; charset=UTF-8\r\n\r\n' +
      JSON.stringify(metadata);
    
    const filePart = delimiter +
      `Content-Type: ${file.type || "application/octet-stream"}\r\n` +
      'Content-Transfer-Encoding: base64\r\n\r\n';

    // Convert file to base64
    const base64File = btoa(
      new Uint8Array(fileContent).reduce((data, byte) => data + String.fromCharCode(byte), '')
    );

    const requestBody = metadataPart + filePart + base64File + closeDelimiter;

    const url = `https://storage.googleapis.com/upload/storage/v1/b/${encodeURIComponent(bucket)}/o?uploadType=multipart`;

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': `multipart/related; boundary=${boundary}`,
        Authorization: `Bearer ${accessToken}`,
      },
      body: requestBody,
    });

    if (!response.ok) {
      throw new Error('Failed to upload image to Google Cloud Storage');
    }

    const data = await response.json();

    if (!includeBlob) {
      return data;
    }

    const gcloudImage = await gcloudGetImage(data.name, false);
    return { ...data, image: gcloudImage };
  };

  const gcloudGetImage = async (filename, filterByEnvironment = true) => {
    if (!accessToken) {
      throw new Error('Authentication credentials not available');
    }

    // First, get metadata to check environment if filtering is enabled
    if (filterByEnvironment) {
      const metadataRes = await fetch(
        `https://storage.googleapis.com/storage/v1/b/${encodeURIComponent(bucket)}/o/${encodeURIComponent(filename)}`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      if (!metadataRes.ok) {
        throw new Error('Failed to get image metadata');
      }

      const metadata = await metadataRes.json();
      
      // Check if image belongs to current environment
      if (metadata.metadata?.environment && metadata.metadata.environment !== environment) {
        throw new Error(`Image belongs to ${metadata.metadata.environment} environment, current environment is ${environment}`);
      }
    }

    // Get the actual image content
    const res = await fetch(
      `https://storage.googleapis.com/storage/v1/b/${encodeURIComponent(bucket)}/o/${encodeURIComponent(filename)}?alt=media`,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      }
    );

    if (!res.ok) {
      throw new Error('Failed to get image from Google Cloud Storage');
    }

    const blob = await res.blob();
    const imageUrl = URL.createObjectURL(blob);

    return { filename, imageUrl, blob };
  }

  const gcloudListImages = async (filterByEnvironment = true) => {
    if (!accessToken) {
      throw new Error('Authentication credentials not available');
    }

    const url = `https://storage.googleapis.com/storage/v1/b/${encodeURIComponent(bucket)}/o`;

    const response = await fetch(url, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    });

    if (!response.ok) {
      throw new Error('Failed to list images from Google Cloud Storage');
    }

    const data = await response.json();
    let items = data.items || [];

    // Filter by environment if requested
    if (filterByEnvironment) {
      items = items.filter(item => {
        // Include images that match current environment OR have no environment metadata (legacy)
        const itemEnvironment = item.metadata?.environment;
        return !itemEnvironment || itemEnvironment === environment;
      });
    }

    return items;
  };

  const gcloudDeleteImage = async (filename, filterByEnvironment = true) => {
    if (!accessToken) {
      throw new Error('Authentication credentials not available');
    }

    // Check environment before deleting if filtering is enabled
    if (filterByEnvironment) {
      const metadataRes = await fetch(
        `https://storage.googleapis.com/storage/v1/b/${encodeURIComponent(bucket)}/o/${encodeURIComponent(filename)}`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      if (!metadataRes.ok) {
        throw new Error('Failed to get image metadata');
      }

      const metadata = await metadataRes.json();
      
      // Check if image belongs to current environment
      if (metadata.metadata?.environment && metadata.metadata.environment !== environment) {
        throw new Error(`Cannot delete image from ${metadata.metadata.environment} environment in ${environment} environment`);
      }
    }

    const res = await fetch(
      `https://storage.googleapis.com/storage/v1/b/${encodeURIComponent(bucket)}/o/${encodeURIComponent(filename)}`,
      {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      }
    );

    if (!res.ok) {
      throw new Error('Failed to delete image from Google Cloud Storage');
    }

    return true;
  };

  return { bucket, environment, gcloudGetImage, gcloudUploadImage, gcloudDeleteImage, gcloudListImages };
}
